import React, { useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../lib/utils';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Loader2, Send, AlertTriangle } from 'lucide-react';
import {
  PAYER_PROGRAMS,
  PROCEDURE_CODES,
  PAYER_TYPES,
} from '../../constants/patientConstants';

const labelFor = (ln) => {
  const pt = PAYER_TYPES.find((p) => p.value === ln.payer_type)?.label || ln.payer_type || '—';
  const pp = PAYER_PROGRAMS.find((p) => p.value === ln.payer_program)?.label || ln.payer_program || '—';
  const pc = PROCEDURE_CODES.find((p) => p.value === ln.procedure_code)?.label || ln.procedure_code || '—';
  return `${pt}  ·  ${pp}  ·  ${pc}`;
};

/**
 * Pre-submit confirm dialog for sending EVV visits to Sandata. Same UX
 * pattern as `SubmitToOdmDialog` for claims — fetches the visits about
 * to be transmitted, lets the user verify or switch each visit's service
 * line, then submits with the (possibly overridden) values.
 *
 * On confirm, applies any per-visit overrides to the visit objects and
 * posts to `/sandata/submit`. No backend change for /sandata/submit was
 * needed — the visits are passed in the request body so we mutate them
 * client-side before sending.
 */
export const SubmitToSandataDialog = ({ open, onClose, onSubmitted }) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // visit_id → { payer_type, payer_program, service_code }
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setVisits([]);
      setOverrides({});
      try {
        const res = await api.post('/sandata/sync-local-visits', null, {
          params: { status_filter: 'Validated' },
        });
        if (!cancelled) {
          if (!res.data?.success || !res.data?.visits_found) {
            toast.info('No validated visits to submit');
            onClose();
            return;
          }
          setVisits(res.data.visits || []);
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || 'Failed to load visits');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, onClose]);

  const setOverride = (visitId, lineIdx) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (lineIdx == null || lineIdx === '__keep__') {
        delete next[visitId];
      } else {
        next[visitId] = lineIdx;
      }
      return next;
    });
  };

  const applyOverridesToPayload = useMemo(() => () => {
    return visits.map((v) => {
      const idx = overrides[v.visit_id];
      if (idx == null) return v;
      const lines = v.patient_billing_lines || [];
      const chosen = lines[parseInt(idx, 10)];
      if (!chosen) return v;
      return {
        ...v,
        service_code: chosen.procedure_code || v.service_code,
        program_code: chosen.payer_program || v.program_code,
        // Persist the picked payer_type alongside so the post-submit
        // historical view shows the right combo.
        payer_type: chosen.payer_type || v.visit_payer_type,
      };
    });
  }, [visits, overrides]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const finalVisits = applyOverridesToPayload();
      const res = await api.post('/sandata/submit', { visits: finalVisits });
      if (res.data?.success) {
        toast.success(`Submitted ${res.data.valid_count} visit${res.data.valid_count === 1 ? '' : 's'} to Sandata`);
        onSubmitted?.(res.data);
        onClose();
      } else {
        toast.error(res.data?.message || 'Submission failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit visits');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="sandata-submit-dialog">
        <DialogHeader>
          <DialogTitle>Submit visits to Sandata</DialogTitle>
          <DialogDescription>
            Verify each visit&rsquo;s service line before transmission.
            Pre-selected to the line the visit was recorded under.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm">Loading validated visits…</span>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-slate-600">
              {visits.length} validated visit{visits.length === 1 ? '' : 's'} ready
            </div>
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-96 overflow-y-auto">
              {visits.map((v) => {
                const lines = v.patient_billing_lines || [];
                const visitCurrent = {
                  payer_type: v.visit_payer_type,
                  payer_program: v.visit_payer_program,
                  procedure_code: v.service_code,
                };
                const matchingIdx = lines.findIndex((ln) =>
                  (ln.payer_type || '') === (visitCurrent.payer_type || '') &&
                  (ln.payer_program || '') === (visitCurrent.payer_program || '') &&
                  (ln.procedure_code || '') === (visitCurrent.procedure_code || '')
                );
                const selectedVal =
                  overrides[v.visit_id] != null
                    ? overrides[v.visit_id]
                    : (matchingIdx >= 0 ? String(matchingIdx) : '__keep__');
                const mismatch = matchingIdx === -1 && lines.length > 0 && overrides[v.visit_id] == null;
                return (
                  <div key={v.visit_id} className="px-3 py-3 text-xs" data-testid={`sandata-visit-${v.visit_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {v.member_last_name}, {v.member_first_name}
                        </p>
                        <p className="text-slate-500 mt-0.5 truncate">
                          {formatDate(v.service_date)} · {v.time_in}–{v.time_out} · Worker {v.worker_first_name} {v.worker_last_name}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 font-mono shrink-0">
                        {v.service_code || '—'}
                      </Badge>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold whitespace-nowrap">
                        Service line
                      </span>
                      <Select value={selectedVal} onValueChange={(val) => setOverride(v.visit_id, val)}>
                        <SelectTrigger className="h-7 text-xs flex-1" data-testid={`sandata-line-trigger-${v.visit_id}`}>
                          <SelectValue placeholder={labelFor(visitCurrent)} />
                        </SelectTrigger>
                        <SelectContent>
                          {lines.map((ln, idx) => (
                            <SelectItem
                              key={`${ln.payer_type || ''}:${ln.payer_program || ''}:${ln.procedure_code || ''}:${idx}`}
                              value={String(idx)}
                            >
                              <span className="inline-flex items-center gap-2 text-xs">
                                {idx === 0 ? (
                                  <span className="text-[9px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5">
                                    Primary
                                  </span>
                                ) : null}
                                <span>{labelFor(ln)}</span>
                              </span>
                            </SelectItem>
                          ))}
                          <SelectItem value="__keep__">
                            <span className="text-slate-500 italic text-xs">
                              {lines.length === 0 ? 'Keep current values' : 'Custom — keep current values'}
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {mismatch ? (
                      <div className="flex items-start gap-1.5 text-[11px] text-amber-800 mt-1">
                        <AlertTriangle className="h-3 w-3 text-amber-600 mt-0.5 shrink-0" />
                        <span>
                          <strong className="font-mono">{labelFor(visitCurrent)}</strong>
                          {' '}doesn&rsquo;t match any of this patient&rsquo;s saved lines. Submit as-is or pick a saved line.
                        </span>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="sandata-submit-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || loading || visits.length === 0}
            data-testid="sandata-submit-confirm"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</>
            ) : (
              <><Send className="h-4 w-4 mr-2" />Submit {visits.length} visit{visits.length === 1 ? '' : 's'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitToSandataDialog;
